import { LitElement, html, nothing } from 'lit';
import { property } from 'lit/decorators.js';
import type { CaveatContext, CaveatVariant } from '../../types/index.js';
import styles from './loquix-caveat-notice.styles.js';

/** Default caveat messages per context. */
const DEFAULT_MESSAGES: Record<CaveatContext, string> = {
  general: 'AI can make mistakes. Check important info.',
  medical: 'This is not medical advice. Consult a healthcare professional.',
  legal: 'This is not legal advice. Consult a qualified attorney.',
  financial: 'This is not financial advice. Consult a financial professional.',
};

/**
 * @tag loquix-caveat-notice
 * @summary Informational notice about AI limitations shown to the user.
 *
 * @csspart notice - The notice container element.
 *
 * @cssprop [--loquix-caveat-text-color] - Text colour.
 * @cssprop [--loquix-caveat-bg] - Background for inline / contextual variants.
 * @cssprop [--loquix-caveat-font-size] - Font size (default 0.6875rem).
 * @cssprop [--loquix-caveat-padding] - Inner padding.
 */
export class LoquixCaveatNotice extends LitElement {
  static styles = [styles];

  /** Custom caveat message. Falls back to a default based on `context`. */
  @property({ type: String })
  message?: string;

  /** Subject-matter context, determines the default message text. */
  @property({ type: String, reflect: true })
  context: CaveatContext = 'general';

  /** Display variant. */
  @property({ type: String, reflect: true })
  variant: CaveatVariant = 'footer';

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  private _resolvedMessage(): string {
    return this.message ?? DEFAULT_MESSAGES[this.context] ?? DEFAULT_MESSAGES.general;
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  private _renderWarningIcon() {
    return html`
      <span class="icon" aria-hidden="true">
        <svg viewBox="0 0 24 24">
          <path
            d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"
          />
        </svg>
      </span>
    `;
  }

  protected render() {
    const msg = this._resolvedMessage();
    const showIcon = this.variant === 'contextual';

    return html`
      <div part="notice" class="notice" role="note" aria-label="${msg}">
        ${showIcon ? this._renderWarningIcon() : nothing}
        <span>${msg}</span>
      </div>
    `;
  }
}
